import { useEffect, useRef } from "react";
import ActionRow from "../components/v2/ActionRow";
import HelperLine from "../components/v2/HelperLine";
import PageTransition from "../components/v2/PageTransition";
import QuestionCounter from "../components/v2/QuestionCounter";
import QuestionLabel from "../components/v2/QuestionLabel";
import TextInput from "../components/v2/TextInput";
import Wordmark from "../components/v2/Wordmark";
import "../components/v2/v2-question-surface.css";

export default function TextareaQuestionSurface({
  current,
  label,
  helper,
  placeholder,
  errorCopy,
  name,
  value,
  error,
  direction = "forward",
  optional = false,
  editMode = false,
  onChange,
  onBlur,
  onBack,
  onNext,
  onSkip,
}) {
  const inputRef = useRef(null);
  const helperId = helper ? `surface-${current}-helper` : undefined;
  const errorId = `surface-${current}-error`;

  useEffect(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, reduceMotion ? 200 : 360);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (error) inputRef.current?.focus();
  }, [error]);

  const hasValue = Boolean(String(value || "").trim());
  const describedBy = [helperId, !optional && error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const actionVariant = editMode ? "done-only" : optional ? "three-button" : "two-button";
  const nextDisabled = editMode ? (!optional && !hasValue) : !hasValue;

  return (
    <section className="tgc-v2-surface tgc-v2-surface--textarea" aria-labelledby="question-label">
      <PageTransition direction={direction}>
        <div className="tgc-v2-content">
          <Wordmark variant="stacked" />
          <QuestionCounter current={current} total={12} />
          <QuestionLabel>{label}</QuestionLabel>
          {helper && <HelperLine id={helperId}>{helper}</HelperLine>}

          <div className={`tgc-v2-field-wrap ${helper ? "tgc-v2-field-wrap--textarea" : "tgc-v2-field-wrap--textarea-no-helper"}`}>
            <TextInput
              ref={inputRef}
              value={value}
              variant="left-aligned"
              multiline
              rows={6}
              error={error}
              name={name}
              autoComplete="off"
              enterKeyHint="enter"
              autoCapitalize="sentences"
              spellCheck="true"
              maxLength={2000}
              placeholder={placeholder}
              aria-labelledby="question-label"
              aria-describedby={describedBy}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
            />
            <div className="tgc-v2-error" id={errorId} aria-live="polite">
              {!optional && error ? errorCopy : ""}
            </div>
          </div>

          <div className={helper ? "tgc-v2-action-row--textarea" : "tgc-v2-action-row--textarea-no-helper"}>
            <ActionRow
              variant={actionVariant}
              onBack={onBack}
              onSkip={onSkip}
              onNext={onNext}
              nextDisabled={nextDisabled}
              nextLabel={editMode ? "Done" : "Next"}
            />
          </div>
        </div>
      </PageTransition>
    </section>
  );
}
